import { useMemo, useState } from "react";
import { object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {
  useEstimateEditorSettings,
  useEstimateEditorStore,
  useEstimateTableSettings,
  useTableExpandHandler,
  TimelineSectionSettings,
} from "@tools";
import { Collapse, Grid, IconButton, Tooltip } from "@material-ui/core";
import { VisibilityOff } from "@material-ui/icons";
import { Alert } from "@components";
import { useStores } from "@hooks";
import { USER_COMPANY_SETTINGS } from "@client";
import { setCompanySettingsQuery } from "@query";
import { TimelineWorkTypeRow } from "./TimelineWorkTypeRow";
import {
  SectionActions,
  TimelineSectionDivider,
  TimelineSectionInner,
} from "./components";
import { TimelineSectionsRowGroupList } from "./TimelineSectionsRowGroupList";

export const TimelineSectionsRow = observer(({ section }) => {
  const { t } = useTranslation();
  const { userStore } = useStores();
  const editorStore = useEstimateEditorStore();
  const [ alertVisible, setAlertVisible ] = useState(false);
  const { allowEdition, blockExpansions } = useEstimateEditorSettings();
  const { fullSectionActions } = useEstimateTableSettings();

  const {
    name,
    color,
    fontColor,
    expanded: sectionExpanded,
    visibleWorkTypes,
  } = section;

  const expandable = useMemo(
    () => allowEdition || visibleWorkTypes.length,
    [ allowEdition, visibleWorkTypes ],
  );

  const expanded = useMemo(
    () => expandable && (sectionExpanded || blockExpansions),
    [ sectionExpanded, blockExpansions, expandable ],
  );

  const handleHide = (event) => {
    event.stopPropagation();
    if (!editorStore.blockedHideTimelineSectionAlert)
      setAlertVisible(true);
    else
      section.setHidden(!section.hidden);
  };

  const handleAlertClose = async (_event, lock) => {
    setAlertVisible(false);
    section.setHidden(!section.hidden);
    if (lock) {
      editorStore.blockedHideTimelineSectionAlert = true;
      await setCompanySettingsQuery(
        USER_COMPANY_SETTINGS.HIDE_TIMELINE_SECTION_ALERT(userStore.userUuid),
        true,
      );
    }
  };

  const { handleOpen, ...collapseProps } = useTableExpandHandler({
    expandable: expandable,
    isOpened: sectionExpanded,
    onExpand: section.setExpanded,
  });

  return (
    <Grid
      item
      container
      className="relative"
      style={{ opacity: section.isTurnedOff ? 0.3 : undefined }}
    >
      <Grid item container wrap="nowrap" className="h-64">
        <TimelineSectionInner
          name={name}
          color={color}
          fontColor={fontColor}
          onColorChange={section.setColor}
          onFontColorChange={section.setFontColor}
          tableDepth={2}
          expanded={expanded}
          expandable={expandable}
          wider={fullSectionActions}
          onExpand={handleOpen}
          actions={
            <SectionActions
              section={section}
              fullActions={fullSectionActions}
              expanded={expanded}
            />
          }
        >
          {allowEdition && <>
            <TimelineSectionSettings section={section} />
            <Tooltip title={t("views.editor.timeline_table.hide_section")}>
              <IconButton
                size="small"
                onClick={handleHide}
                className="color-error hoverIcon transparent-3 text-sm p-0-5"
                aria-label={t("views.editor.timeline_table.hide_section")}
              >
                <VisibilityOff />
              </IconButton>
            </Tooltip>
          </>}
        </TimelineSectionInner>
      </Grid>
      <Collapse
        in={expanded}
        timeout="auto"
        mountOnEnter
        unmountOnExit
        className="w-full"
        {...collapseProps}
      >
        <TimelineSectionsRowGroupList
          listElements={visibleWorkTypes}
          onReorder={section.reorderWorkTypes}
          disabled={!allowEdition}
          group={`workTypes-${section.id}`}
        >
          {visibleWorkTypes.map((workType) => (
            <TimelineWorkTypeRow
              key={workType.id}
              workType={workType}
              section={section}
            />
          ))}
        </TimelineSectionsRowGroupList>
      </Collapse>
      <TimelineSectionDivider tableDepth={2} />
      <Alert
        isOpen={alertVisible}
        title={t("views.editor.timeline_table.section_hidden_alert.title")}
        acceptText={t("common.continue")}
        onAccept={handleAlertClose}
        useLockCheckbox
      >
        {t("views.editor.timeline_table.section_hidden_alert.text")}
      </Alert>
    </Grid>
  );
});

TimelineSectionsRow.propTypes = {
  section: object.isRequired,
};
